import { StringOrNull } from 'store/entity/types';
import { IServerUser, IUser, IUserStatus } from 'store/users/usersType';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as MakhsanApi from 'api/MakhsanApi';
import {
  getProviderStatus,
  getUserStatus,
  registerUser,
  submitDevice,
  user_updateUserInfoBasedOnJWT,
  getFlags,
  addOrUpdateUserFlag,
} from 'api/ManApi';
import { RootState, TThunkApi } from 'store/store';
import {
  loadingProviderData,
  updateProviderData,
} from '../providers/providerSlice';
import { addUser, updateUserFn } from '../users/usersSlice';
import { IProvider } from '../providers/providerTypes';
import unregisterServiceWorkers from '../../utils/unregisterServiceWorkers';
import { getRedirectToLoginLocation } from '../../utils/happyFunctions';
import { navigate } from '@reach/router';
import UserMixpanel from '../../utils/mixpanel/MixpanelClass';

type Permissions = 'Create_User' | 'Edit_User';

export interface IUserFlag {
  id: number;
  flagName: string;
  flagValue: string;
  flagGroup: string;
}

interface ICustomProvider {
  id?: number;
  isLoading?: boolean;
  provider_active: string | null;
  provider_register: string | null;
}

export interface IThisUser {
  name: string;
  email?: string;
  phone: StringOrNull;
  active: string | null;
  userId: string;
  register: string;
  provider: ICustomProvider;
  expireDate: number;
  issueDate: number;
  providerId?: number;
  isLoggedIn: boolean;
  permissions: Permissions[];
  access_token: string;
  firebaseToken?: string;
  provider_active?: boolean;
  provider_register?: boolean;
  jwtData?: any;
}

export interface IThisUserServer {
  email: string;
  phone: StringOrNull;
  roles: string[];
  userId: string;
  issueDate: number;
  expireDate: number;
  accessToken: string;
  permissions: Permissions[];
}

export interface IThisUserState {
  isLoggedIn: boolean;
  userId: IThisUserServer['userId'];
  access_token?: string;
  expireDate?: number;
  provider: ICustomProvider;
  providerId: null | number;
  provider_active: IProvider['status']['active'] | null;
  provider_register: IProvider['status']['register'] | null;
  phone: IThisUserServer['phone'] | 'invalid';
  register: IUserStatus['register'] | null;
  active: IUserStatus['active'] | null;
  firebaseToken?: string;
  name: string;
  email?: string;
  permissions?: Permissions[];
  issueDate?: number;
  useLocalStorage?: boolean;
  jwtData?: any;
  userFlags: { [flagGroup: string]: IUserFlag[] };
}

const guestUser: IThisUserState = {
  isLoggedIn: false,
  userId: '',
  access_token: undefined,
  provider: {
    isLoading: false,
    id: undefined,
    provider_register: null,
    provider_active: null,
  },
  providerId: null,
  provider_active: null,
  provider_register: null,
  phone: 'invalid',
  register: null,
  active: null,
  name: '',
  userFlags: {},
};

class User {
  id: IUser['id'];
  name: IUser['name'];
  phoneNumber: IUser['phoneNumber'];
  userStatus: IUser['userStatus'];
  deviceInfo: IUser['deviceInfo'];
  constructor(
    id: IUser['id'],
    name: IUser['name'],
    phoneNumber: IUser['phoneNumber'],
    userStatus: IUser['userStatus'],
    deviceInfo: IUser['deviceInfo']
  ) {
    this.id = id;
    this.name = name;
    this.phoneNumber = phoneNumber;
    this.userStatus = userStatus;
    this.deviceInfo = deviceInfo;
  }
}

export const storeForThisUserWithTempLogin = localStorage;
const getInitialState = () => {
  let thisUser = storeForThisUserWithTempLogin.getItem('thisUser');
  if (!thisUser || !JSON.parse(thisUser).isLoggedIn)
    thisUser = localStorage.getItem('thisUser');
  if (thisUser === null) {
    return guestUser;
  }
  try {
    const parsedThisUser = JSON.parse(thisUser) as IThisUserState;

    // Ensure userFlags is initialized for backward compatibility
    if (!parsedThisUser.userFlags) {
      parsedThisUser.userFlags = {};
    }

    return parsedThisUser.expireDate && parsedThisUser.expireDate > Date.now()
      ? parsedThisUser
      : guestUser;
  } catch (_) {
    return guestUser;
  }
};

const initialState = getInitialState();

function updateUserStatusHandler(
  state: IThisUserState,
  action: PayloadAction<{ user: IServerUser }>
) {
  state = {
    ...state,
    ...action.payload.user.userStatus,
  };
}

function userLoggedInHandler(
  state: IThisUserState,
  action: PayloadAction<{ thisUser: IThisUserServer; stayLoggedIn: boolean }>
) {
  const { accessToken, roles, ...rest } = action.payload.thisUser;

  localStorage.setItem('thisUser', JSON.stringify(action.payload.thisUser));

  state = {
    ...state,
    ...rest,
    ...{
      register: null,
      providerId: null,
      provider: {
        provider_register: null,
        provider_active: null,
      },
      isLoggedIn: true,
      provider_active: null,
      provider_register: null,
    },
    useLocalStorage: action.payload.stayLoggedIn,
    access_token: accessToken,
    userFlags: {},
  };

  return state;
}

function setProviderStatusHandler(
  state: IThisUserState,
  action: PayloadAction<{ provider: IProvider }>
) {
  state.provider = {
    isLoading: false,
    id: action.payload.provider.id,
    provider_register: action.payload.provider.status.register,
    provider_active: action.payload.provider.status.active,
  };
}

function logoutUserHandler(
  state: IThisUserState,
  action: PayloadAction<{ shouldRedirect?: boolean; redirect?: string }>
) {
  const { shouldRedirect = false, redirect } = action.payload;
  storeForThisUserWithTempLogin.removeItem('thisUser');
  localStorage.removeItem('thisUser');
  state = guestUser;

  if (shouldRedirect) {
    const mixpanelUser = UserMixpanel.getInstance();
    mixpanelUser.sendLogoutTrack();
    unregisterServiceWorkers();
    if (!redirect) {
      navigate('/login');
    } else window.location.href = getRedirectToLoginLocation();
  }

  return state;
}

function clearAccessTokenAndIsLoggedInHandler(
  state: IThisUserState,
  action: PayloadAction<{ shouldLogOut?: boolean } | undefined>
) {
  state.isLoggedIn = false;
  state.access_token = '';
  if (action.payload?.shouldLogOut) {
    logoutUserHandler(state, {
      payload: { shouldRedirect: true },
      type: 'thisUser/logoutUserHandler',
    });
  }
}

function setFirebaseTokenHandler(
  state: IThisUserState,
  action: PayloadAction<{ token: string }>
) {
  state.firebaseToken = action.payload.token;
}

function changeUserPhoneNumberHandler(
  state: IThisUserState,
  action: PayloadAction<{ phoneNumber: string }>
) {
  state.phone = action.payload.phoneNumber;
}

function changeUserNameHandler(
  state: IThisUserState,
  action: PayloadAction<{ name: string }>
) {
  state.name = action.payload.name;
}

function setSentPhoneNumberHandler(
  state: IThisUserState,
  action: PayloadAction<{ phone: string }>
) {
  state.phone = action.payload.phone;
}

function renewTokenHandler(
  state: IThisUserState,
  action: PayloadAction<{ accessToken: string; expireDate: number }>
) {
  state.access_token = action.payload.accessToken;
  state.expireDate = action.payload.expireDate;
}

function setUserFlagsHandler(
  state: IThisUserState,
  action: PayloadAction<{ flags: IUserFlag[]; flagGroup?: string }>
) {
  const { flags, flagGroup } = action.payload;
  if (flagGroup) {
    state.userFlags[flagGroup] = flags;
  } else {
    // Group flags by flagGroup
    flags.forEach((flag) => {
      if (!state.userFlags[flag.flagGroup]) {
        state.userFlags[flag.flagGroup] = [];
      }
      const existingIndex = state.userFlags[flag.flagGroup].findIndex(
        (f) => f.id === flag.id
      );
      if (existingIndex >= 0) {
        state.userFlags[flag.flagGroup][existingIndex] = flag;
      } else {
        state.userFlags[flag.flagGroup].push(flag);
      }
    });
  }
}

function updateUserFlagHandler(
  state: IThisUserState,
  action: PayloadAction<{ flag: IUserFlag }>
) {
  const { flag } = action.payload;
  if (!state.userFlags[flag.flagGroup]) {
    state.userFlags[flag.flagGroup] = [];
  }
  const existingIndex = state.userFlags[flag.flagGroup].findIndex(
    (f) => f.id === flag.id
  );
  if (existingIndex >= 0) {
    state.userFlags[flag.flagGroup][existingIndex] = flag;
  } else {
    state.userFlags[flag.flagGroup].push(flag);
  }
}

// Thunks
export const registerThisUserIfNotRegisteredFn = async (
  {
    userId,
    name,
    phone,
    accessToken,
  }: {
    userId: string;
    name: string;
    phone: StringOrNull;
    accessToken?: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  const storeWithToken = accessToken
    ? {
        ...store,
        thisUser: {
          ...store.thisUser,
          access_token: accessToken,
        },
      }
    : store;

  return getUserStatus(storeWithToken)
    .then(({ data: user }) => {
      if (user.userStatus.register === 'NotRegistered') {
        let newUser;
        if (typeof userId === undefined) {
          newUser = new User(
            Number(store.thisUser.userId!),
            '',
            store.thisUser.phone,
            { active: 'Enable', register: 'Registered' },
            null
          );
        } else {
          newUser = new User(
            Number(userId),
            name,
            phone,
            { active: 'Enable', register: 'Registered' },
            null
          );
        }

        return registerUser(newUser as IUser, storeWithToken)
          .then((res) => {
            ThunkApi.dispatch(updateUserStatus({ user: res.data }));

            // Save complete user info including access token after registration
            if (accessToken) {
              const userInfo: IThisUserServer = {
                userId: userId,
                accessToken: accessToken,
                phone: phone,
                email: store.thisUser.email || '',
                roles: [],
                permissions: store.thisUser.permissions || [],
                issueDate: store.thisUser.issueDate || Date.now(),
                expireDate: store.thisUser.expireDate || Date.now() + 86400000,
              };
              ThunkApi.dispatch(
                userLoggedIn({ thisUser: userInfo, stayLoggedIn: true })
              );
            }

            return Promise.resolve({
              user: res.data,
            });
          })
          .catch((err) => {
            return ThunkApi.rejectWithValue(err) as any;
          });
      } else {
        ThunkApi.dispatch(checkProviderStatus({}));
        ThunkApi.dispatch(updateUserStatus({ user: user }));

        if (accessToken && !store.thisUser.access_token) {
          const userInfo: IThisUserServer = {
            userId: userId,
            accessToken: accessToken,
            phone: phone,
            email: store.thisUser.email || '',
            roles: [],
            permissions: store.thisUser.permissions || [],
            issueDate: store.thisUser.issueDate || Date.now(),
            expireDate: store.thisUser.expireDate || Date.now() + 86400000,
          };
          ThunkApi.dispatch(
            userLoggedIn({ thisUser: userInfo, stayLoggedIn: true })
          );
        }

        return Promise.resolve({
          user: user,
        });
      }
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const registerThisUserIfNotRegistered = createAsyncThunk(
  'thisUser/registerThisUserIfNotRegistered',
  registerThisUserIfNotRegisteredFn
);

export const checkProviderStatusFn = async (_: any, ThunkApi: TThunkApi) => {
  const store = ThunkApi.getState() as RootState;
  const currentProviderId = store.thisUser.provider.id;
  const providerIsValid = currentProviderId
    ? store.providers?.map[currentProviderId]?.isValid
    : false;

  if (providerIsValid) {
    return Promise.resolve(undefined);
  }

  if (currentProviderId) {
    ThunkApi.dispatch(loadingProviderData({ providerId: currentProviderId }));
  }

  return getProviderStatus(store)
    .then(({ data }) => {
      ThunkApi.dispatch(setProviderStatus({ provider: data }));
      return Promise.resolve({ provider: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const checkProviderStatus = createAsyncThunk(
  'thisUser/checkProviderStatus',
  checkProviderStatusFn
);

const loginUserAndCheckAuthentication = async (
  thisUser: IThisUserServer,
  userId: string,
  staySignedIn: boolean,
  ThunkApi: TThunkApi
) => {
  if (thisUser.userId !== userId)
    ThunkApi.dispatch(logoutUser({ shouldRedirect: false }));
  ThunkApi.dispatch(userLoggedIn({ thisUser, stayLoggedIn: staySignedIn }));
  await ThunkApi.dispatch(
    registerThisUserIfNotRegistered({
      userId: thisUser.userId,
      name: '',
      phone: thisUser.phone,
      accessToken: thisUser.accessToken,
    })
  );
};

export const handleUpdateUserInfoJWT = async (
  service: number,
  state: RootState,
  thisUser: IThisUserServer,
  jwtToken: string
) => {
  // will update user info
  await user_updateUserInfoBasedOnJWT(
    service,
    thisUser.accessToken,
    jwtToken,
    state
  );
};

export const enterWithSingleAccessCodeFn = async (
  {
    loginToken,
    codeNumber,
    staySignedIn,
  }: { loginToken: string; codeNumber: string; staySignedIn: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const accessCode = loginToken + codeNumber;

  return MakhsanApi.enterWithSingleAccessCode(accessCode, staySignedIn)
    .then(async ({ data: thisUser }) => {
      const userId = store.thisUser.userId;
      await loginUserAndCheckAuthentication(
        thisUser,
        userId,
        staySignedIn,
        ThunkApi
      );
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

export const enterWithJWTFn = async (
  {
    token,
    serviceId,
    decodedData,
  }: { token: string; serviceId: number; decodedData: any },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return MakhsanApi.loginWithJWT(token, serviceId).then(
    async ({ data: authResponse }) => {
      const userId = store.thisUser.userId;
      authResponse.authInfo.jwtData = decodedData;
      await loginUserAndCheckAuthentication(
        authResponse.authInfo,
        userId,
        false,
        ThunkApi
      );
      await updateUserFn(
        {
          user: {
            id: Number(userId),
            name:
              authResponse.userInfo.Name + ' ' + authResponse.userInfo.Family,
            phoneNumber: authResponse.userInfo.PhoneM,
            userStatus: {
              active: 'Enable',
              register: 'Registered',
            },
          } as any,
        },
        ThunkApi
      );
      // await handleUpdateUserInfoJWT(
      //   serviceId,
      //   store,
      //   authResponse.authInfo,
      //   token
      // );
    }
  );
};

const enterWithJWT = createAsyncThunk('thisUser/enterWithJWT', enterWithJWTFn);

const enterWithSingleAccessCode = createAsyncThunk(
  'thisUser/enterWithSingleAccessCode',
  enterWithSingleAccessCodeFn
);

export const loginWithUsernameAndPasswordFn = async (
  {
    username,
    password,
    staySignedIn,
  }: {
    username: string;
    password: string;
    staySignedIn: boolean;
  },
  ThunkApi: TThunkApi
) => {
  return MakhsanApi.authenticate(username, password, staySignedIn).then(
    async ({ data: thisUser }) => {
      ThunkApi.dispatch(userLoggedIn({ thisUser, stayLoggedIn: staySignedIn }));
      await ThunkApi.dispatch(
        registerThisUserIfNotRegistered({
          userId: thisUser.userId,
          name: '',
          phone: thisUser.phone,
        })
      );
    }
  );
};
const loginWithUsernameAndPassword = createAsyncThunk(
  'thisUser/loginWithUsernameAndPassword',
  loginWithUsernameAndPasswordFn
);

export const authenticate2FAFn = async (
  {
    phone,
    email,
    password,
    staySignedIn = false,
    dryRun = false,
  }: {
    phone: string | null;
    email: string | null;
    password: string | null;
    staySignedIn: boolean;
    dryRun: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return MakhsanApi.authenticate2FA(
    phone,
    email,
    password,
    staySignedIn,
    dryRun
  )
    .then(
      async ({ data: response }) => {
        if (response.authInfo) {
          const userId = store.thisUser.userId;
          if (response.authInfo.userId !== userId)
            ThunkApi.dispatch(logoutUser({ shouldRedirect: false }));
          ThunkApi.dispatch(
            userLoggedIn({
              thisUser: response.authInfo,
              stayLoggedIn: staySignedIn,
            })
          );
          await ThunkApi.dispatch(
            registerThisUserIfNotRegistered({
              userId: response.authInfo.userId,
              name: '',
              phone: response.authInfo.phone,
            })
          );
        }

        return Promise.resolve({ data: response });
      },
      (err) => {
        return ThunkApi.rejectWithValue(err) as any;
      }
    )
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const authenticate2FA = createAsyncThunk(
  'thisUser/authenticate2FA',
  authenticate2FAFn
);

export const registerWithUsernameAndPasswordFn = async (
  {
    username,
    password,
    captcha,
  }: {
    username: string;
    password: string;
    captcha: string;
  },
  ThunkApi: TThunkApi
) => {
  try {
    const res = await MakhsanApi.secureRegister(username, password, captcha);
    await ThunkApi.dispatch(
      userLoggedIn({ thisUser: res.data, stayLoggedIn: true })
    );
    return await ThunkApi.dispatch(
      registerThisUserIfNotRegistered({
        userId: res.data.userId,
        name: '',
        phone: res.data.phone,
        accessToken: res.data.accessToken,
      })
    );
  } catch (e: any) {
    throw new Error(e.data.message);
  }
};

const registerWithUsernameAndPassword = createAsyncThunk(
  'thisUser/registerWithUsernameAndPassword',
  registerWithUsernameAndPasswordFn
);

export const submitSingleDeviceFn = async (
  { name, token }: { name: string; token: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  submitDevice(store, name, token).then(() => {
    ThunkApi.dispatch(setFirebaseToken({ token: token }));
  });
};
const submitSingleDevice = createAsyncThunk(
  'thisUser/submitSingleDevice',
  submitSingleDeviceFn
);

export const requestChangePhoneNumberFn = async (
  { phoneNumber }: { phoneNumber: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  MakhsanApi.requestChangePhoneNumber(phoneNumber, store)
    .then((res) => {
      return Promise.resolve({ token: res.data.token });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requestChangePhoneNumber = createAsyncThunk(
  'thisUser/requestChangePhoneNumber',
  requestChangePhoneNumberFn
);

export const validatePhoneNumberFn = async (
  {
    secret,
    token,
    phoneNumber,
  }: { secret: string; token: string; phoneNumber: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return MakhsanApi.validatePhoneNumber(store, token, secret)
    .then(() => {
      const userId = Number(store.thisUser.userId);
      const user = store.users.map[userId];
      user.phoneNumber = phoneNumber;

      ThunkApi.dispatch(changeUserPhoneNumber({ phoneNumber: phoneNumber }));
      ThunkApi.dispatch(addUser({ user: user }));

      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const validatePhoneNumber = createAsyncThunk(
  'thisUser/validatePhoneNumber',
  validatePhoneNumberFn
);

export const refreshTokenFn = async (_: any, ThunkApi: TThunkApi) => {
  const store = ThunkApi.getState() as RootState;
  return MakhsanApi.renewToken(store)
    .then((res) => {
      ThunkApi.dispatch(
        renewToken({
          accessToken: res.data.accessToken,
          expireDate: res.data.expireDate,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const refreshToken = createAsyncThunk('thisUser/refreshToken', refreshTokenFn);

const setProviderStatus = createAsyncThunk(
  'thisUser/setProviderStatus',
  async ({ provider }: { provider: IProvider }, ThunkApi: TThunkApi) => {
    ThunkApi.dispatch(setProviderStatusAction({ provider }));

    if (provider.status.register === 'Registered') {
      ThunkApi.dispatch(updateProviderData({ provider }));
    }
  }
);

export const getUserFlagsFn = async (
  { flagGroup }: { flagGroup?: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return getFlags(store, undefined, flagGroup)
    .then(({ data }) => {
      return Promise.resolve({ flags: data, flagGroup });
    })
    .catch((err: any) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const getUserFlags = createAsyncThunk('thisUser/getUserFlags', getUserFlagsFn);

export const setAddOrUpdateUserFlagFn = async (
  {
    flagName,
    flagValue,
    flagGroup,
  }: { flagName: string; flagValue: string; flagGroup: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return addOrUpdateUserFlag(store, flagName, flagValue, flagGroup)
    .then(({ data }) => {
      return Promise.resolve({ flag: data });
    })
    .catch((err: any) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const setAddOrUpdateUserFlag = createAsyncThunk(
  'thisUser/addOrUpdateUserFlag',
  setAddOrUpdateUserFlagFn
);

const thisUserSlice = createSlice({
  name: 'thisUser',
  initialState: initialState,
  reducers: {
    updateUserStatus: updateUserStatusHandler,
    userLoggedIn: userLoggedInHandler,
    setProviderStatusAction: setProviderStatusHandler,
    logoutUser: logoutUserHandler,
    clearAccessTokenAndIsLoggedIn: clearAccessTokenAndIsLoggedInHandler,
    setFirebaseToken: setFirebaseTokenHandler,
    changeUserPhoneNumber: changeUserPhoneNumberHandler,
    changeUserName: changeUserNameHandler,
    setSentPhoneNumber: setSentPhoneNumberHandler,
    renewToken: renewTokenHandler,
  },
  extraReducers: (builder) => {
    builder.addCase(getUserFlags.fulfilled, (state, action) => {
      setUserFlagsHandler(state, action);
    });
    builder.addCase(setAddOrUpdateUserFlag.fulfilled, (state, action) => {
      updateUserFlagHandler(state, action);
    });
  },
});

export const {
  updateUserStatus,
  userLoggedIn,
  setProviderStatusAction,
  logoutUser,
  clearAccessTokenAndIsLoggedIn,
  setFirebaseToken,
  changeUserPhoneNumber,
  changeUserName,
  setSentPhoneNumber,
  renewToken,
} = thisUserSlice.actions;

export {
  registerThisUserIfNotRegistered,
  checkProviderStatus,
  enterWithSingleAccessCode,
  loginWithUsernameAndPassword,
  authenticate2FA,
  registerWithUsernameAndPassword,
  submitSingleDevice,
  requestChangePhoneNumber,
  validatePhoneNumber,
  refreshToken,
  setProviderStatus,
  enterWithJWT,
  getUserFlags,
  setAddOrUpdateUserFlag,
};

export default thisUserSlice.reducer;
